import { ISelectOption, Select } from '@salecycle/ui-component-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSetSubjectLine } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/actions/useSetSubjectLine';
import { TargetAudienceType } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/CampaignEditorState';
import { useCampaignEditorContext } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/hooks/useCampaignEditorContext';
import { getFirstNonEmptyValue } from 'sections/utils/objectHelpers';
import { useGetSubjectLines } from 'server/subject-lines/queries/getSubjectLines/useGetSubjectLines';

export interface SubjectLineSelectProps {
  targetAudience: TargetAudienceType;
}

export const SubjectLineSelect = ({ targetAudience }: SubjectLineSelectProps) => {
  const { t, i18n } = useTranslation('campaigns');
  const { siteId } = useParams<{ siteId: string }>();

  const { data } = useGetSubjectLines({ siteId });
  const { state } = useCampaignEditorContext();

  const audience = state.campaign.targetAudience.find(s => s.audience === targetAudience)?.audience;

  const setSubjectLine = useSetSubjectLine();

  const defaultSelectedOption = useMemo(() => {
    const subjectLine = state.campaign.subjectLine[audience];
    if (subjectLine) {
      const label: ISelectOption<string> = {
        value: subjectLine.id,
        label: subjectLine.label,
      };

      return label;
    }
  }, [audience, state.campaign.subjectLine]);

  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);

  useEffect(() => {
    setSelectedOption(defaultSelectedOption);
  }, [defaultSelectedOption]);

  const options: ISelectOption<string>[] = useMemo(() => {
    return data?.map(s => ({
      value: s.id,
      label: getFirstNonEmptyValue(s.subjectLines, i18n.language),
    }));
  }, [data, i18n.language]);

  const changeOption = useCallback(
    (option: ISelectOption<string>) => {
      setSelectedOption(option);
      setSubjectLine(audience, { id: option.value, label: option.label });
    },
    [audience, setSubjectLine],
  );

  return (
    <Select
      inputId="subject-line"
      ariaLabel={t('bulkRetargetingCampaigns.editor.subjectLinesSelectAria')}
      label={t('bulkRetargetingCampaigns.editor.subjectLinesSelectAria')}
      placeholder={t('bulkRetargetingCampaigns.editor.subjectLinesSelectPlaceholder')}
      options={options}
      value={!!selectedOption?.value ? selectedOption : null}
      onChange={changeOption}
      spacingBelow={false}
      disabled={state?.readOnly}
    />
  );
};
